import { type ReactNode } from 'react';
import { Badge, Group, Stack, Text, Tooltip } from '@wapharma/design-system';
import { SessionClientEnvironmentIcons } from './SessionClientEnvironmentIcons';
import type { CustomerSessionLocation } from './CustomerSessionLocationCard';
import { FriendlyDate } from './FriendlyDate';
import cardStyles from './CustomerSessionLocationCard.module.css';

export type SessionConnectionDetails = CustomerSessionLocation & {
  createdAt?: string;
  expiresAt?: string;
  revokedAt?: string | null;
  status?: 'live' | 'active' | 'expired' | 'revoked';
};

function openStreetMapSearchUrl(city: string | null, country: string | null): string | null {
  const q = [city, country].filter(Boolean).join(', ').trim();
  if (!q) return null;
  return `https://www.openstreetmap.org/search?query=${encodeURIComponent(q)}`;
}

function formatCoordinates(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

function isLoopbackIp(ip: string | null | undefined): boolean {
  const v = (ip?.trim().toLowerCase() ?? '').replace(/\s+/g, '');
  return v === '::1' || v === '127.0.0.1' || v === 'localhost';
}

function TechDetailRow({ label, children }: { label: string; children: ReactNode }) {
  if (children == null || children === false) return null;
  return (
    <div className={cardStyles.techRow}>
      <Text size="xs" c="dimmed" className={cardStyles.techLabel}>
        {label}
      </Text>
      <div className={cardStyles.techValue}>{children}</div>
    </div>
  );
}

/** Short place label for table cells. */
export function formatSessionLocationShort(session: CustomerSessionLocation): string {
  const city = session.city?.trim() || null;
  const region = session.region?.trim() || null;
  const country = session.country?.trim() || null;
  const label = session.locationLabel?.trim() || null;
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (region && country) return `${region}, ${country}`;
  if (country) return country;
  if (isLoopbackIp(session.ip) || isLoopbackIp(label)) return 'Machine locale';
  if (label) return label;
  if (session.latitude != null && session.longitude != null) return 'Position IP';
  return 'Lieu inconnu';
}

export function SessionConnectionDetailsPanel({ session }: { session: SessionConnectionDetails }) {
  const city = session.city?.trim() || null;
  const country = session.country?.trim() || null;
  const region = session.region?.trim() || null;
  const ip = session.ip?.trim() || null;
  const hasCoords = session.latitude != null && session.longitude != null;
  const hasPlace = !!(city || country || region || session.locationLabel?.trim());
  const showUnresolvedPlace = !hasCoords && !hasPlace;
  const searchUrl = openStreetMapSearchUrl(city, country);
  const revoked = session.status === 'revoked' || Boolean(session.revokedAt);

  return (
    <div className={cardStyles.techPanel} role="region" aria-label="Détails de session">
      <Text size="xs" fw={600} c="dimmed" className={cardStyles.techPanelTitle}>
        Connexion & appareil
      </Text>
      <Stack gap="sm" className={cardStyles.techStack}>
        {session.createdAt ? (
          <TechDetailRow label="Créée">
            <FriendlyDate iso={session.createdAt} />
          </TechDetailRow>
        ) : null}
        {revoked && session.revokedAt ? (
          <TechDetailRow label="Révoquée">
            <FriendlyDate iso={session.revokedAt} />
          </TechDetailRow>
        ) : session.expiresAt ? (
          <TechDetailRow label="Expire">
            <FriendlyDate iso={session.expiresAt} />
          </TechDetailRow>
        ) : null}
        <TechDetailRow label="Adresse IP">
          {ip ? (
            <span className={cardStyles.timestamp}>{ip}</span>
          ) : (
            <Text size="xs" c="dimmed">
              —
            </Text>
          )}
        </TechDetailRow>
        <TechDetailRow label="Environnement">
          <SessionClientEnvironmentIcons
            userAgent={session.userAgent}
            deviceType={session.deviceType}
            deviceLabel={session.deviceLabel}
          />
        </TechDetailRow>
        <TechDetailRow label="Localisation (IP)">
          {showUnresolvedPlace && !hasPlace ? (
            <Text size="xs" c="dimmed">
              {isLoopbackIp(ip) ? 'Machine locale (pas de géoloc IP)' : 'Ville non résolue depuis l’IP'}
            </Text>
          ) : null}
          {hasPlace ? (
            <Group gap="xs" wrap="wrap">
              {city ? (
                <Badge purpose="type" tone="neutral">
                  {city}
                </Badge>
              ) : null}
              {region ? (
                <Badge purpose="type" tone="neutral">
                  {region}
                </Badge>
              ) : null}
              {country ? (
                <Badge purpose="type" tone="neutral">
                  {country}
                </Badge>
              ) : null}
              {!city && !country && session.locationLabel?.trim() ? (
                <Text size="sm">{session.locationLabel.trim()}</Text>
              ) : null}
            </Group>
          ) : null}
        </TechDetailRow>
        {hasCoords ? (
          <TechDetailRow label="Coordonnées">
            <span className={`${cardStyles.timestamp} ${cardStyles.coords}`}>
              {formatCoordinates(session.latitude!, session.longitude!)}
            </span>
            <Text size="xs" c="dimmed" mt={4}>
              Position approximative (géolocalisation IP)
            </Text>
          </TechDetailRow>
        ) : null}
        {searchUrl ? (
          <a className={cardStyles.searchLink} href={searchUrl} target="_blank" rel="noopener noreferrer">
            Rechercher sur la carte
          </a>
        ) : null}
        {session.userAgentSummary ? (
          <TechDetailRow label="Agent utilisateur">
            <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
              {session.userAgentSummary}
            </Text>
          </TechDetailRow>
        ) : null}
      </Stack>
    </div>
  );
}

/** Table cell: place label; hover reveals IP / machine / location / expiry card. */
export function SessionLocationHoverCell({ session }: { session: SessionConnectionDetails }) {
  const short = formatSessionLocationShort(session);
  return (
    <Tooltip wide portal position="bottom" label={<SessionConnectionDetailsPanel session={session} />}>
      <button type="button" className={cardStyles.locationHoverTrigger} aria-label={`Lieu : ${short}`}>
        <Text size="sm" className={cardStyles.locationHoverLabel}>
          {short}
        </Text>
      </button>
    </Tooltip>
  );
}
